import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { products, reviews } from '../data';

export default function Home() {
  const featuredProducts = products.slice(0, 3);
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.5], [0, 150]);

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }}
      className="pt-20 md:pt-24"
    >
      {/* Hero Section */}
      <section className="min-h-[90vh] md:min-h-[85vh] flex flex-col justify-center px-6 container mx-auto relative overflow-hidden">
        <motion.div style={{ y: heroY }} className="relative z-10 mt-10 md:mt-0">
          <motion.h1 
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="text-fluid-hero mb-8 md:mb-12 relative z-20"
          >
            FORM <br className="hidden md:block"/> <span className="text-crimson">&</span> FUNCTION
          </motion.h1>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end relative z-20">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-6 lg:col-span-5 aspect-[4/5] md:aspect-[3/4] w-full overflow-hidden bg-gray-200 relative"
          >
            <img 
              src="https://images.unsplash.com/photo-1490481651871-ab68de25d43d?q=80&w=2070&auto=format&fit=crop" 
              alt="Editorial presentation" 
              className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-[2s] ease-out"
            />
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="md:col-span-6 lg:col-span-5 lg:col-start-7 pb-4 md:pb-12"
          >
            <p className="text-lg md:text-2xl lg:text-3xl max-w-md mb-8 leading-snug text-graphite/90">
              Capsule 01 establishes the foundation of the modern wardrobe. Stark, uncompromising, essential.
            </p>
            <Link 
              to="/collection" 
              className="group inline-flex items-center gap-4 text-xs uppercase tracking-widest font-bold"
            >
              <span className="border-b-2 border-graphite pb-1 group-hover:text-crimson group-hover:border-crimson transition-colors">
                Explore Collection
              </span>
              <span className="group-hover:translate-x-2 transition-transform text-crimson">→</span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 md:py-32 px-6 container mx-auto">
        <div className="flex flex-col md:flex-row md:justify-between md:items-end mb-12 md:mb-16 border-b border-graphite/20 pb-6 gap-4">
          <h2 className="text-fluid-h2">CORE PIECES</h2>
          <Link to="/collection" className="text-xs uppercase tracking-widest font-bold hover:text-crimson transition-colors self-start md:self-auto">
            View All Collection
          </Link>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {featuredProducts.map((product, index) => (
            <motion.div 
              key={product.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: index * 0.15, duration: 0.8, ease: "easeOut" }}
              className="group cursor-pointer flex flex-col"
            >
              <Link to="/collection" className="block w-full">
                <div className="aspect-[3/4] overflow-hidden bg-bone border border-graphite/10 mb-6 relative">
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    className="w-full h-full object-cover transition-transform duration-[1.5s] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-graphite/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  {/* Quick view badge */}
                  <div className="absolute bottom-6 left-1/2 -translate-x-1/2 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                    <span className="bg-bone text-graphite px-6 py-3 text-xs uppercase tracking-widest font-bold shadow-lg">
                      Discover
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-base md:text-lg font-medium tracking-tight group-hover:text-crimson transition-colors">{product.name}</h3>
                    <p className="text-xs uppercase tracking-widest text-graphite/50 mt-2">{product.category}</p>
                  </div>
                  <span className="text-sm font-medium">${product.price}</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Reviews Banner */}
      <section className="bg-graphite text-bone py-24 md:py-32 px-6">
        <div className="container mx-auto">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xs uppercase tracking-widest text-crimson mb-16 text-center"
          >
            Clientele Perspectives
          </motion.h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-12">
            {reviews.map((review, index) => (
              <motion.div 
                key={review.id} 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                className="flex flex-col items-center text-center"
              >
                <div className="flex gap-1 mb-6 text-crimson text-sm">
                  {[...Array(review.rating)].map((_, i) => <span key={i}>★</span>)}
                </div>
                <p className="text-lg md:text-xl lg:text-2xl font-serif italic mb-8 text-bone/90 leading-relaxed max-w-sm">
                  "{review.text}"
                </p>
                <span className="text-xs uppercase tracking-widest font-bold text-bone/50">— {review.author}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </motion.div>
  );
}